export const DELHI_STATE_CODE = "07";

export const cleanGstNumber = (gst) =>
  (gst || "").toString().toUpperCase().replace(/[^A-Z0-9]/g, "");

export const getGstStateCode = (gst) => {
  const normalized = cleanGstNumber(gst);
  const stateCode = normalized.slice(0, 2);
  return /^\d{2}$/.test(stateCode) ? stateCode : "";
};

export const round2 = (value) => {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return Math.round(parsed * 100) / 100;
};

export const parseAmount = (value, fallback = 0) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const cleaned = value.toString().replace(/[^0-9.-]/g, "");
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const normalizeItemsForTotals = (items = []) =>
  (Array.isArray(items) ? items : []).map((item = {}) => {
    const quantity = parseAmount(item.quantity, 0);
    const rate = parseAmount(item.rate, 0);
    const taxableValue = parseAmount(
      item.taxable_value ?? item.basic_amount ?? item.amount ?? quantity * rate,
      0,
    );
    const discountPercent = round2(parseAmount(item.discount_amount, 0));
    const discountValue = round2((taxableValue * discountPercent) / 100);
    const explicitNetAmount =
      item.net_amount !== undefined && item.net_amount !== ""
        ? parseAmount(item.net_amount, Number.NaN)
        : Number.NaN;
    const shouldUseExplicitNetAmount =
      Number.isFinite(explicitNetAmount) &&
      !(explicitNetAmount === 0 && (taxableValue > 0 || discountPercent > 0));
    const netAmount = round2(
      shouldUseExplicitNetAmount
        ? explicitNetAmount
        : Math.max(taxableValue - discountValue, 0),
    );
    const gstRate = parseAmount(item.gst_rate, 0);
    const computedTax = round2((netAmount * gstRate) / 100);
    const computedTotalValue = round2(netAmount + computedTax);
    const explicitTotal = parseAmount(
      item.total_value ?? item.total ?? item.line_total ?? item.gross_amount,
      Number.NaN,
    );
    const shouldUseExplicitTotal =
      Number.isFinite(explicitTotal) &&
      !(explicitTotal === 0 && (netAmount > 0 || gstRate > 0));
    const totalValue = shouldUseExplicitTotal
      ? explicitTotal
      : computedTotalValue;
    const gstAmount = round2(Math.max(totalValue - netAmount, computedTax));

    return {
      ...item,
      taxable_value: round2(taxableValue),
      discount_percent: discountPercent,
      discount_amount: discountValue,
      net_amount: netAmount,
      gst_rate: round2(gstRate),
      gross_amount: round2(totalValue),
      total_value: round2(totalValue),
      gst_amount: gstAmount,
    };
  });

export const sumItemTotals = (items = []) =>
  normalizeItemsForTotals(items).reduce(
    (acc, item) => ({
      basic_amount: round2(acc.basic_amount + item.taxable_value),
      discount_amount: round2(acc.discount_amount + item.discount_amount),
      net_payable: round2(acc.net_payable + item.net_amount),
      gst: round2(acc.gst + item.gst_amount),
      gross_amount: round2(acc.gross_amount + item.gross_amount),
      total_amount: round2(acc.total_amount + item.total_value),
    }),
    {
      basic_amount: 0,
      discount_amount: 0,
      net_payable: 0,
      gst: 0,
      gross_amount: 0,
      total_amount: 0,
    },
  );

export const deriveGstBreakdown = ({
  invoiceType = "purchase",
  companyGstNo = "",
  extractedData = {},
}) => {
  const companyStateCode = getGstStateCode(companyGstNo);
  const counterpartyGst =
    invoiceType === "purchase"
      ? cleanGstNumber(
          extractedData.supplier_gst_no ||
            extractedData.supplier_gst ||
            extractedData.gst_no,
        )
      : cleanGstNumber(extractedData.buyer_gst_no || extractedData.buyer_gst);
  const counterpartyStateCode = getGstStateCode(counterpartyGst);
  const itemTotals = sumItemTotals(extractedData.items || []);
  const hasItems =
    itemTotals.basic_amount > 0 ||
    itemTotals.gst > 0 ||
    itemTotals.total_amount > 0;

  const explicitBreakupGst = round2(
    parseAmount(extractedData.cgst, 0) +
      parseAmount(extractedData.sgst, 0) +
      parseAmount(extractedData.igst, 0),
  );

  let basicAmount;
  let discountValue;
  let netPayable;
  let gstAmount;
  let grossAmount;
  let totalAmount;

  if (hasItems) {
    // Line items are the single source of truth: every amount total is summed
    // from the items so the panel recalculates live as item values change.
    basicAmount = round2(itemTotals.basic_amount);
    netPayable = round2(itemTotals.net_payable);
    discountValue = round2(Math.max(basicAmount - netPayable, 0));
    gstAmount = round2(
      extractedData.gst_manual_override === true
        ? parseAmount(extractedData.gst, explicitBreakupGst)
        : itemTotals.gst,
    );
    grossAmount = round2(netPayable + gstAmount);
    totalAmount = grossAmount;
  } else {
    // No line items: fall back to the manually entered header amounts.
    basicAmount = round2(parseAmount(extractedData.basic_amount, 0));
    const hasExplicitNetPayable =
      extractedData.net_payable !== undefined &&
      extractedData.net_payable !== null &&
      extractedData.net_payable !== "";
    const hasExplicitGross =
      extractedData.gross_amount !== undefined &&
      extractedData.gross_amount !== null &&
      extractedData.gross_amount !== "";
    const hasExplicitTotal =
      extractedData.total_amount !== undefined &&
      extractedData.total_amount !== null &&
      extractedData.total_amount !== "";
    discountValue = round2(parseAmount(extractedData.discount_amount, 0));
    netPayable = round2(
      hasExplicitNetPayable
        ? parseAmount(
            extractedData.net_payable,
            Math.max(basicAmount - discountValue, 0),
          )
        : Math.max(basicAmount - discountValue, 0),
    );
    gstAmount = round2(parseAmount(extractedData.gst, explicitBreakupGst));
    grossAmount = round2(
      hasExplicitGross
        ? parseAmount(extractedData.gross_amount, netPayable + gstAmount)
        : netPayable + gstAmount,
    );
    totalAmount = round2(
      hasExplicitTotal
        ? parseAmount(extractedData.total_amount, grossAmount)
        : grossAmount,
    );
  }

  const canCompareStates = Boolean(companyStateCode && counterpartyStateCode);
  const isIntraState = canCompareStates && companyStateCode === counterpartyStateCode;
  const gstBreakdownType = canCompareStates
    ? isIntraState
      ? "cgst_sgst"
      : "igst"
    : parseAmount(extractedData.igst, 0) > 0
      ? "igst"
      : "cgst_sgst";

  const cgst = gstBreakdownType === "cgst_sgst" ? round2(gstAmount / 2) : 0;
  const sgst = gstBreakdownType === "cgst_sgst" ? round2(gstAmount - cgst) : 0;
  const igst = gstBreakdownType === "igst" ? round2(gstAmount) : 0;

  return {
    basic_amount: basicAmount,
    discount_amount: discountValue,
    net_payable: netPayable,
    gross_amount: grossAmount,
    gst: gstAmount,
    total_amount: totalAmount,
    cgst,
    sgst,
    igst,
    gst_breakdown_type: gstBreakdownType,
    company_gst_state_code: companyStateCode,
    counterparty_gst_state_code: counterpartyStateCode,
    is_delhi_counterparty: counterpartyStateCode === DELHI_STATE_CODE,
  };
};
